import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { Textarea } from "@/components/core/forms/textarea";
import { WEBHOOK_EVENTS, type WebhookEndpoint, editWebhookEndpointSchema, webhookEndpointSchema } from "@/features/dashboard/types/webhook-endpoint";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCopy, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

function generateSecret(): string {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

interface WebhookEndpointFormProps {
	endpoint?: WebhookEndpoint;
	onSubmit: (values: WebhookEndpoint) => Promise<void>;
	isSubmitting?: boolean;
}

export function WebhookEndpointForm({ endpoint, onSubmit }: WebhookEndpointFormProps) {
	const isEditing = !!endpoint;
	const [showSecretField, setShowSecretField] = useState(!isEditing);

	const form = useForm<WebhookEndpoint>({
		resolver: zodResolver(isEditing ? editWebhookEndpointSchema : webhookEndpointSchema),
		defaultValues: endpoint
			? { ...endpoint, secret: "" }
			: {
					url: "",
					secret: generateSecret(),
					events: ["project.created", "project.updated"],
					description: "",
					is_active: true,
				},
	});

	const handleSubmit = (values: WebhookEndpoint) => {
		const data = { ...values };
		if (isEditing && !showSecretField) {
			(data as Record<string, unknown>).secret = undefined;
		}
		return onSubmit(data);
	};

	return (
		<Form {...form}>
			<form id="webhook-endpoint-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
					<div className="col-span-12">
						<FormField
							control={form.control}
							name="url"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>Webhook URL</FormLabel>
									<FormGroup>
										<Input className="input" placeholder="https://other-system.example.com/api/webhooks" {...field} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="col-span-12">
						{isEditing && !showSecretField ? (
							<div>
								<FormLabel>Shared Secret</FormLabel>
								<div className="mt-1 flex items-center gap-2">
									<span className="text-sm text-gray-500 font-mono">••••••••••••••••</span>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											setShowSecretField(true);
											form.setValue("secret", generateSecret());
										}}
									>
										<IconRefresh size={16} />
										Change Secret
									</Button>
								</div>
							</div>
						) : (
							<FormField
								control={form.control}
								name="secret"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Shared Secret</FormLabel>
										<FormGroup>
											<div className="flex gap-2">
												<Input className="input font-mono text-sm flex-1" placeholder="Enter a strong shared secret (min 16 chars)" {...field} />
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="shrink-0"
													onClick={() => {
														navigator.clipboard.writeText(field.value || "");
														toast.success("Secret copied to clipboard");
													}}
													title="Copy to clipboard"
												>
													<IconCopy size={16} />
												</Button>
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="shrink-0"
													onClick={() => field.onChange(generateSecret())}
													title="Generate random secret"
												>
													<IconRefresh size={16} />
													Generate
												</Button>
											</div>
										</FormGroup>
										<FormMessage />
										{!isEditing && <p className="text-xs text-gray-500 mt-1">Save this secret — it will be used to sign outbound requests.</p>}
										{isEditing && (
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="mt-1 text-xs"
												onClick={() => {
													setShowSecretField(false);
													form.setValue("secret", "");
												}}
											>
												Cancel — keep existing secret
											</Button>
										)}
									</FormItem>
								)}
							/>
						)}
					</div>
					<div className="col-span-12">
						<FormField
							control={form.control}
							name="events"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>Events</FormLabel>
									<div className="flex flex-col gap-2 mt-1">
										{WEBHOOK_EVENTS.map((event) => (
											<label key={event.value} className="flex items-center gap-2 cursor-pointer">
												<input
													type="checkbox"
													checked={field.value?.includes(event.value)}
													onChange={(e) => {
														const current = field.value || [];
														if (e.target.checked) {
															field.onChange([...current, event.value]);
														} else {
															field.onChange(current.filter((v: string) => v !== event.value));
														}
													}}
													className="rounded border-gray-300"
												/>
												<span className="text-sm">{event.label}</span>
												<span className="text-xs text-gray-400 font-mono">({event.value})</span>
											</label>
										))}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="col-span-12">
						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Description</FormLabel>
									<FormGroup>
										<Textarea className="input" placeholder="e.g., Sync projects to Fleet Ops" rows={2} {...field} value={field.value || ""} />
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>
			</form>
		</Form>
	);
}
